import { ApiProperty } from '@nestjs/swagger';
import { ControlDusItemDto } from './control-dus-item.dto';

/**
 * DTO para la respuesta paginada de Control DUS.
 * Retorna lista de DUS con información de paginación.
 *
 * @remarks
 * - La paginación es server-side obligatoriamente
 * - Solo se retornan los registros solicitados (no toda la data)
 * - Incluye metadatos de paginación para el frontend
 *
 * @example
 * ```typescript
 * const response: ControlDusResponseDto = {
 *   data: [
 *     { nroIE: 6213, cliente: 'MONFRUT', dus: '13009996-3', ... },
 *     { nroIE: 6214, cliente: 'ACOGRAPES', dus: '13009997-4', ... },
 *   ],
 *   pagination: {
 *     total: 125,
 *     page: 1,
 *     limit: 10,
 *     totalPages: 13
 *   },
 *   timestamp: '2025-01-24T12:00:00.000Z'
 * };
 * ```
 */
export class ControlDusResponseDto {
  /**
   * Lista de DUS con información completa.
   * Retorna exactamente la cantidad solicitada en el filtro (limit).
   *
   * @example [{ nroIE: 6213, cliente: 'MONFRUT', ... }]
   */
  @ApiProperty({
    description: 'Lista de DUS',
    type: [ControlDusItemDto],
    isArray: true,
  })
  data!: ControlDusItemDto[];

  /**
   * Información de paginación para el frontend.
   * Incluye total de registros, página actual, registros por página y total de páginas.
   */
  @ApiProperty({
    description: 'Información de paginación',
    example: {
      total: 125,
      page: 1,
      limit: 10,
      totalPages: 13,
    },
  })
  pagination!: {
    /**
     * Total de registros que cumplen con los filtros aplicados.
     *
     * @example 125
     */
    total: number;

    /**
     * Página actual solicitada.
     *
     * @example 1
     */
    page: number;

    /**
     * Cantidad de registros por página.
     *
     * @example 10
     */
    limit: number;

    /**
     * Total de páginas calculadas (total / limit).
     *
     * @example 13
     */
    totalPages: number;
  };

  /**
   * Timestamp de la respuesta en formato ISO 8601.
   * Útil para verificar la frescura de los datos en el frontend.
   *
   * @example '2025-01-24T12:00:00.000Z'
   */
  @ApiProperty({
    description: 'Timestamp de la respuesta',
    example: '2025-01-24T12:00:00.000Z',
  })
  timestamp!: string;
}
